import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import { MarkerIcon, markerSizes, backendToDisplay } from './MarkerStyles';
import MarkerPopupContent from './MarkerPopupContent';
import ReactDOMServer from 'react-dom/server';

// Component to handle map click events
const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng);
    },
  });
  return null;
};

// Component to handle map size invalidation
const MapSizeHandler = () => {
  const map = useMap();
  const isMounted = useRef(true);

  // Helper to safely invalidate size
  const safeInvalidateSize = useCallback(() => {
    if (isMounted.current && map && map.getContainer()) {
      try {
        map.invalidateSize();
      } catch (err) {
        console.warn('Map resize warning:', err);
      }
    }
  }, [map]);

  useEffect(() => {
    isMounted.current = true;

    // Small delay to ensure container is fully rendered
    const timer = setTimeout(() => {
      safeInvalidateSize();
    }, 100);

    return () => {
      isMounted.current = false;
      clearTimeout(timer);
    };
  }, [safeInvalidateSize]);

  // Handle window resize and custom resize events
  useEffect(() => {
    const handleResize = () => {
      // Small delay to ensure layout is complete
      setTimeout(() => {
        safeInvalidateSize();
      }, 50);
    };

    window.addEventListener('resize', handleResize);
    // Listen for custom map resize events
    window.addEventListener('mapResize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mapResize', handleResize);
    };
  }, [safeInvalidateSize]);

  // We removed the interval that was forcing resize every 1s 
  // as it causes performance issues and errors when unmounted.
  // If dynamic resizing is needed, it should be triggered by events.

  return null;
};

// Component to track map view changes (zoom, pan)
const MapViewTracker = ({ onViewChange }) => {
  const map = useMapEvents({
    moveend: () => {
      if (onViewChange) {
        const center = map.getCenter();
        onViewChange({
          zoom: map.getZoom(),
          center: [center.lat, center.lng]
        });
      }
    },
  });

  // Report initial view on mount
  useEffect(() => {
    if (onViewChange) {
      const center = map.getCenter();
      onViewChange({
        zoom: map.getZoom(),
        center: [center.lat, center.lng]
      });
    }
  }, [map, onViewChange]);

  return null;
};

const TimelineMap = ({
  markers = [],
  onMapClick,
  onMarkerDelete,
  onMarkerDragEnd,
  onMarkerSizeChange,
  onMarkerClick,
  onViewChange,
  initialZoom,
  initialCenter
}) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Calculate map center and zoom based on markers (or use initial values if provided)
  const mapSettings = useMemo(() => {
    // If initial values are provided (from URL params), use them
    if (initialZoom !== undefined && initialCenter) {
      return {
        center: initialCenter,
        zoom: initialZoom
      };
    }

    if (!markers || markers.length === 0) {
      // Default view - world center
      return { center: [0, 0], zoom: 3 };
    }

    // Calculate bounds from all markers
    const lats = markers.map(m => m.lngLat[0]);
    const lngs = markers.map(m => m.lngLat[1]);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    // Calculate center
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    // Calculate zoom based on bounds
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    let zoom = 3;
    // if (maxDiff < 0.01) zoom = 15;
    // else if (maxDiff < 0.1) zoom = 12;
    // else if (maxDiff < 1) zoom = 8;
    // else if (maxDiff < 10) zoom = 5;
    // else if (maxDiff < 50) zoom = 3;
    return {
      center: [centerLat, centerLng],
      zoom: 3
    };
  }, [markers, initialZoom, initialCenter]);

  // Create map icons for markers
  const createMapIcon = useCallback((marker) => {
    const size = markerSizes[marker.size || 'm'] || markerSizes['m'] || 32;
    const color = marker.color || (marker.storyEvent_id?.category_id?.color) || '#ef4444';

    const iconElement = MarkerIcon(marker.type, color, marker.size || 'm');

    return L.divIcon({
      className: 'custom-marker',
      html: ReactDOMServer.renderToStaticMarkup(iconElement),
      iconSize: [size, size],
      iconAnchor: [size / 2, 3 * size / 4],
      popupAnchor: [0, -size / 2]
    });
  }, []);

  if (!isClient) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        <p className="ml-4 text-lg text-gray-600">Harita yükleniyor...</p>
      </div>
    );
  }

  return (
    <MapContainer
      center={mapSettings.center}
      zoom={mapSettings.zoom}
      scrollWheelZoom={true}
      attributionControl={false}
      className="w-full h-full"
      style={{ zIndex: 1 }}
    >
      <MapSizeHandler />
      <TileLayer
        url="/tiles/{z}/{x}/{y}.png"
        maxNativeZoom={8}
        maxZoom={8}
      />

      {/* Track map view changes for embed functionality */}
      {onViewChange && <MapViewTracker onViewChange={onViewChange} />}

      {/* Add MapClickHandler to listen for map clicks */}
      {onMapClick && <MapClickHandler onMapClick={onMapClick} />}

      {/* Render markers */}
      {markers.map((marker) => (
        <Marker
          key={marker._id || marker.id}
          position={[marker.lngLat[0], marker.lngLat[1]]}
          icon={createMapIcon(marker)}
          draggable={!!onMarkerDragEnd}
          eventHandlers={{
            ...(onMarkerDragEnd ? {
              dragend: (e) => {
                const newPosition = [e.target.getLatLng().lat, e.target.getLatLng().lng];
                onMarkerDragEnd(marker._id || marker.id, newPosition);
              }
            } : {}),
            ...(onMarkerClick ? {
              click: () => {
                onMarkerClick(marker.storyEvent_id || marker.event);
              }
            } : {})
          }}
        >
          {!onMarkerClick && (
            <Popup>
              <MarkerPopupContent
                marker={marker}
                onDelete={onMarkerDelete}
                onSizeChange={onMarkerSizeChange}
              />
            </Popup>
          )}
        </Marker>
      ))}
    </MapContainer>
  );
};

export default TimelineMap; 